import * as React from 'react';
import { cn } from '@relay-towing/tokens';
import {
  centerRecipe,
  dividerRecipe,
  gridColumns,
  gridRecipe,
  inlineRecipe,
  pageRecipe,
  publicShellBodyRecipe,
  publicShellRecipe,
  SHOW_CLASSES,
  stackRecipe,
  type PublicShellBodyRecipeProps,
  type PublicShellRecipeProps,
  type ShowBreakpoint,
  type ShowDisplay,
  type CenterRecipeProps,
  type GridCols,
  type GridRecipeProps,
  type InlineRecipeProps,
  type PageRecipeProps,
  type StackRecipeProps,
} from '../recipes/layout.recipe';
import { pageHeaderRecipe } from '../recipes/surface.recipe';
import { Text } from './Text';

export interface StackProps
  extends React.HTMLAttributes<HTMLDivElement>,
    StackRecipeProps {
  as?: React.ElementType;
}

/**
 * Vertical layout with spacing from a named scale.
 *
 * Prefer this over `space-y-*`: two screens both using `gap={4}` are
 * guaranteed to match, and the scale can be retuned centrally.
 *
 * `grow` makes it the flex child that takes the leftover room and truncates
 * rather than pushing the row wide. `divided` draws a rule between children
 * and, unless told otherwise, closes the gap so the rule sits between rows
 * rather than above each one. `as="form"` is how a form gets its spacing.
 * `animated` is for a box whose width or place changes with state — a
 * collapsing column, a sliding panel — so the change eases instead of jumping.
 * `dropTarget` is for a row in a drag-to-reorder list: `true` draws the drop
 * indicator along its top edge, `false` reserves the indicator's space so
 * nothing shifts, and leaving it unset draws nothing.
 */
export const Stack = React.forwardRef<HTMLDivElement, StackProps>(
  function Stack(
    {
      as: Component = 'div',
      className,
      gap,
      align,
      justify,
      wrap,
      grow,
      divided,
      animated,
      dropTarget,
      ...props
    },
    ref
  ) {
    return (
      <Component
        ref={ref}
        className={cn(
          stackRecipe({
            gap: gap ?? (divided ? 0 : undefined),
            align,
            justify,
            wrap,
            grow,
            divided,
            animated,
            dropTarget,
          }),
          className
        )}
        {...props}
      />
    );
  }
);

export interface InlineProps
  extends React.HTMLAttributes<HTMLDivElement>,
    InlineRecipeProps {
  as?: React.ElementType;
}

/** Horizontal layout. Same spacing scale as Stack. */
export const Inline = React.forwardRef<HTMLDivElement, InlineProps>(
  function Inline(
    {
      as: Component = 'div',
      className,
      gap,
      align,
      justify,
      wrap,
      grow,
      animated,
      dropTarget,
      ...props
    },
    ref
  ) {
    return (
      <Component
        ref={ref}
        className={cn(
          inlineRecipe({
            gap,
            align,
            justify,
            wrap,
            grow,
            animated,
            dropTarget,
          }),
          className
        )}
        {...props}
      />
    );
  }
);

export interface ShowProps extends React.HTMLAttributes<HTMLElement> {
  /** Render only from this breakpoint up. */
  above?: ShowBreakpoint;
  /** Render only below this breakpoint. */
  below?: ShowBreakpoint;
  /** What the element is when shown. Only matters with `above`. */
  display?: ShowDisplay;
  as?: React.ElementType;
}

/**
 * Content for one side of a breakpoint.
 *
 *   <Button>
 *     <Upload />
 *     <Show above="sm">Upload photos</Show>
 *     <Show below="sm">Photos</Show>
 *   </Button>
 *
 * The long label on a desktop and the short one on a phone. The app wrote
 * this pair 22 times as `hidden sm:inline` / `sm:hidden` spans, all but one
 * of them inside a Button. Both halves stay in the DOM; only CSS decides,
 * which is what keeps the button the same element on both.
 */
export const Show = React.forwardRef<HTMLElement, ShowProps>(function Show(
  {
    above,
    below,
    display = 'inline',
    as: Component = 'span',
    className,
    ...props
  },
  ref
) {
  return (
    <Component
      ref={ref}
      className={cn(
        above ? SHOW_CLASSES.above[display][above] : undefined,
        below ? SHOW_CLASSES.below[below] : undefined,
        className
      )}
      {...props}
    />
  );
});

export interface PublicShellProps
  extends React.HTMLAttributes<HTMLDivElement>,
    PublicShellRecipeProps,
    PublicShellBodyRecipeProps {
  /** Centred above the content: the product mark on the sign-in card. */
  logo?: React.ReactNode;
}

/**
 * The frame around a screen outside the dashboard shell — sign-in, sign-up,
 * an invitation, an opt-in, the account-error page.
 *
 *   <PublicShell logo={<img src={mark} alt="Relay Tow" className="size-12" />}>
 *     <Card>…</Card>
 *   </PublicShell>
 *
 * Full viewport height, everything centred, one column of a named width.
 * Five files had this on four different backgrounds; the point of naming it
 * is that they were meant to be the same place.
 */
export const PublicShell = React.forwardRef<HTMLDivElement, PublicShellProps>(
  function PublicShell(
    { tone, width, logo, className, children, ...props },
    ref
  ) {
    return (
      <div
        ref={ref}
        className={cn(publicShellRecipe({ tone }), className)}
        {...props}
      >
        <div className={publicShellBodyRecipe({ width })}>
          {logo ? (
            <div className="relay-ui-internal mb-6 flex justify-center">
              {logo}
            </div>
          ) : null}
          {children}
        </div>
      </div>
    );
  }
);

export interface DividerProps extends React.HTMLAttributes<HTMLDivElement> {
  orientation?: 'horizontal' | 'vertical';
}

/** A rule between sections. */
export const Divider = React.forwardRef<HTMLDivElement, DividerProps>(
  function Divider({ className, orientation = 'horizontal', ...props }, ref) {
    return (
      <div
        ref={ref}
        role="separator"
        aria-orientation={orientation}
        className={cn(dividerRecipe({ orientation }), className)}
        {...props}
      />
    );
  }
);

export interface PageHeaderProps
  extends Omit<React.HTMLAttributes<HTMLDivElement>, 'title'> {
  title: React.ReactNode;
  description?: React.ReactNode;
  /** Eyebrow text above the title — breadcrumb-ish context. */
  eyebrow?: React.ReactNode;
  /** Primary and secondary buttons for the screen. */
  actions?: React.ReactNode;
  /** Draw a rule beneath the header. */
  divided?: boolean;
  /**
   * `section` titles a panel inside a tab rather than the tab itself, so it
   * renders an h2. The settings panels are all sections of the Settings tab.
   */
  level?: 'page' | 'section';
}

/**
 * The title block at the top of a screen.
 *
 * Replaces the hand-rolled `<div className="relay-ui-internal flex justify-between"><h2
 * className="text-3xl font-bold tracking-tight">…` pattern, so every page
 * title is the same size and every action row sits in the same place.
 */
export const PageHeader = React.forwardRef<HTMLDivElement, PageHeaderProps>(
  function PageHeader(
    {
      title,
      description,
      eyebrow,
      actions,
      divided,
      level = 'page',
      className,
      ...props
    },
    ref
  ) {
    return (
      <div
        ref={ref}
        className={cn(pageHeaderRecipe({ divided }), className)}
        {...props}
      >
        <div className="relay-ui-internal min-w-0 space-y-1">
          {eyebrow ? (
            <Text variant="overline" tone="muted">
              {eyebrow}
            </Text>
          ) : null}
          <Text variant={level === 'section' ? 'h2' : 'h1'}>{title}</Text>
          {description ? (
            <Text variant="body" tone="muted">
              {description}
            </Text>
          ) : null}
        </div>
        {actions ? (
          <div className="relay-ui-internal flex shrink-0 flex-wrap items-center gap-2">
            {actions}
          </div>
        ) : null}
      </div>
    );
  }
);

export interface PageProps
  extends Omit<React.HTMLAttributes<HTMLDivElement>, 'title'>,
    PageRecipeProps {
  /** Omit for a screen that draws its own heading, or has none. */
  title?: React.ReactNode;
  description?: React.ReactNode;
  eyebrow?: React.ReactNode;
  actions?: React.ReactNode;
  divided?: boolean;
  level?: PageHeaderProps['level'];
}

/**
 * A whole screen: the header, then the content, spaced the same as every
 * other screen.
 *
 *   <Page title="Drivers" description="…" actions={<Button>Add</Button>}>
 *     …
 *   </Page>
 *
 * This is the shape 15 of the app's 18 PageHeader call sites already had, half
 * written as `<Stack gap={6}>` and half as `<div className="relay-ui-internal space-y-6">` —
 * the same 24px either way. Page is the one spelling.
 *
 * `fill` is for the screens that must fit the viewport rather than scroll the
 * page: the header keeps its height and the content gets the rest. Reach for
 * it instead of guessing at `h-[calc(100vh-4rem)]`, which hard-codes a header
 * height the shell is free to change. `fill="lg"` does the same from `lg` up
 * only, for a list-beside-record screen whose columns stack on a phone.
 */
export const Page = React.forwardRef<HTMLDivElement, PageProps>(function Page(
  {
    className,
    gap,
    fill,
    title,
    description,
    eyebrow,
    actions,
    divided,
    level,
    children,
    ...props
  },
  ref
) {
  return (
    <div
      ref={ref}
      className={cn(pageRecipe({ gap, fill }), className)}
      {...props}
    >
      {title ? (
        <PageHeader
          title={title}
          description={description}
          eyebrow={eyebrow}
          actions={actions}
          divided={divided}
          level={level}
        />
      ) : null}
      {/* Only when filling: the content has to be the flex child that takes
          the leftover height, and it cannot be `children` themselves. A
          column, so one child that asks to grow (a Tabs with `fill`) gets
          the height rather than sitting in a block it cannot measure. */}
      {fill ? (
        <div
          className={
            fill === 'lg'
              ? 'relay-ui-internal lg:flex lg:min-h-0 lg:flex-1 lg:flex-col'
              : 'relay-ui-internal flex min-h-0 flex-1 flex-col'
          }
        >
          {children}
        </div>
      ) : (
        children
      )}
    </div>
  );
});

export interface GridProps
  extends React.HTMLAttributes<HTMLDivElement>,
    GridRecipeProps {
  as?: React.ElementType;
  /**
   * A number is that many columns at every width. An object collapses:
   * `{ base: 1, md: 2 }` is one column on a phone and two from `md` up.
   */
  cols?: GridCols;
}

/**
 * A column grid.
 *
 *   <Grid cols={2}>…</Grid>                    two columns, always
 *   <Grid cols={{ base: 1, md: 2 }}>…</Grid>   one on a phone, two from md
 *
 * `cols={2}` deliberately does NOT collapse on small screens. The app has 128
 * hand-written grids and 88 of them keep their columns on a phone — 37 of
 * those hold form fields, which is almost certainly a bug, but the component
 * cannot tell a deliberate two-up from an accidental one. Making the number
 * mean what it says keeps the accidents visible as `cols={2}` on a form,
 * rather than silently restyling every screen the way a built-in collapse
 * would. Spell the collapse out and it is in the diff where someone can see it.
 */
export const Grid = React.forwardRef<HTMLDivElement, GridProps>(function Grid(
  { as, cols, gap, align, className, ...props },
  ref
) {
  const Component = (as ?? 'div') as React.ElementType;
  return (
    <Component
      ref={ref}
      className={cn(gridRecipe({ gap, align }), gridColumns(cols), className)}
      {...props}
    />
  );
});

export interface CenterProps
  extends React.HTMLAttributes<HTMLDivElement>,
    CenterRecipeProps {
  as?: React.ElementType;
}

/**
 * Content centred in the space it has.
 *
 *   <Center fill="parent">…</Center>
 *   <Center pad="md" direction="column" gap={2}>…</Center>
 *
 * `fill="parent"` is `h-full`, which resolves to `auto` inside a parent whose
 * height is only a minimum — put the height on the Center itself in that case.
 * That has caught people out; `Loading` is this with a spinner in it.
 */
export const Center = React.forwardRef<HTMLDivElement, CenterProps>(
  function Center({ as, direction, pad, fill, gap, className, ...props }, ref) {
    const Component = (as ?? 'div') as React.ElementType;
    return (
      <Component
        ref={ref}
        className={cn(centerRecipe({ direction, pad, fill, gap }), className)}
        {...props}
      />
    );
  }
);
